import {
  IProductSelectionAvailability,
  IProductSelectionAvailabilityMap,
} from '@wix/wixstores-client-core/dist/es/src/productVariantCalculator/ProductVariantCalculator';
import {
  calcSelectionsAvailability,
  composeCachedMethods,
} from '@wix/wixstores-client-core/dist/es/src/productVariantCalculator/SelectionsAvailabilityCalculator';
import {IProduct as GraphqlProduct} from '@wix/wixstores-graphql-schema';
import {IProduct} from '../types/galleryTypes';

export type VariantInfo = {variantSelectionIds: number[]; selectionsAvailability: IProductSelectionAvailabilityMap};

const hasNoVisibleVariantsCache = new WeakMap<IProduct, {productItems: IProduct['productItems']; value: boolean}>();

export const hasNoVisibleVariants = ({product, isCatalogV3}: {product: IProduct; isCatalogV3: boolean}): boolean => {
  if (!product) {
    return false;
  }

  const cached = hasNoVisibleVariantsCache.get(product);
  if (cached && cached.productItems === product.productItems) {
    return cached.value;
  }

  const value = calcHasNoVisibleVariants({product, isCatalogV3});
  hasNoVisibleVariantsCache.set(product, {productItems: product.productItems, value});
  return value;
};

const calcHasNoVisibleVariants = ({product, isCatalogV3}: {product: IProduct; isCatalogV3: boolean}): boolean => {
  if (!product.isManageProductItems || !product.options?.length) {
    return false;
  }

  if (isCatalogV3 && product.minPriceVariant) {
    return !product.minPriceVariant.id;
  }

  if (product.productItems?.some((productItem) => productItem.isVisible !== false)) {
    return false;
  }

  const graphqlProduct = product as GraphqlProduct;
  const cachedMethods = composeCachedMethods(graphqlProduct);
  const variantBoundOptions = (
    product.productItems?.length ? cachedMethods.getNonModifiersOptions() : product.options
  ) as IProduct['options'];

  if (!variantBoundOptions?.length) {
    return false;
  }

  const availability = calcSelectionsAvailability({product: graphqlProduct, variantSelectionIds: [], cachedMethods});

  return variantBoundOptions.some(
    (option) =>
      option.selections?.length > 0 &&
      option.selections.every((selection) => availability[option.id]?.[selection.id]?.isVisible === false)
  );
};

const somePickedSelection = (
  product: IProduct,
  variantInfo: VariantInfo | undefined,
  predicate: (availability: IProductSelectionAvailability | undefined) => boolean
): boolean => {
  const variantSelectionIds = variantInfo?.variantSelectionIds;

  if (!variantSelectionIds?.length || !product?.options?.length) {
    return false;
  }

  return product.options.some((option) =>
    option.selections?.some(
      (selection) =>
        variantSelectionIds.includes(selection.id) &&
        predicate(variantInfo.selectionsAvailability?.[option.id]?.[selection.id])
    )
  );
};

export const isSelectedCombinationOutOfStock = (product: IProduct, variantInfo: VariantInfo | undefined): boolean =>
  somePickedSelection(
    product,
    variantInfo,
    (availability) => availability?.isVisible !== false && availability?.isSelectable === false
  );

export const isSelectedCombinationHidden = (product: IProduct, variantInfo: VariantInfo | undefined): boolean =>
  somePickedSelection(product, variantInfo, (availability) => availability?.isVisible === false);
